import { buyNowQueryParamsSessionStorage, envs, urlParams } from '@ctct/infrastructure-layer';

import { CentralizedUrlAdapter } from '../adapters/CentralizedUrlAdapter';
import {
  BillingFormSubmissionActionsController,
  BillingInfoFormController,
  InboundPromoCodeApplicationController,
  OrderSummaryController,
  PaymentButtonController,
  PaymentMethodsController,
  PricingConfiguratorController,
  UpdateBillingController,
  PayflowTrackActionsController,
  PromoCodesController,
} from '../controllers';
import { OrderSummarySetupController } from '../controllers/OrderSummarySetupController';
import { initializerHelpers } from '../helpers';
import type { Listeners } from '../helpers';
import {
  UpdateBillingManager,
  PromoCodesManager,
  ThankYouManager,
  BillingInfoFormManager,
  PaymentButtonManager,
  PaymentMethodsManager,
  EmailPlansManager,
  RouterNonUiManager,
  OrderSummaryManager,
  AlertManager,
  PrepaySelectManager,
  ContactsTierSelectManager,
  NonProfitManager,
  CurrencyManager,
  PricingConfiguratorManager,
  PaymentProcessingInfoManager,
  PayflowTopBarManager,
  InboundPromoCodesManager,
} from '../managers';

export class BuyNowInitializer {
  public listeners: Listeners;

  // managers
  private paymentButton: PaymentButtonManager;

  private pricingConfigurator: PricingConfiguratorManager;

  private prepaySelect: PrepaySelectManager;

  private contactsTierSelect: ContactsTierSelectManager;

  private currency: CurrencyManager;

  private nonProfit: NonProfitManager;

  private billingInfoForm: BillingInfoFormManager;

  private paymentMethods: PaymentMethodsManager;

  private thankYou: ThankYouManager;

  private emailPlans: EmailPlansManager;

  private router: RouterNonUiManager;

  private orderSummary: OrderSummaryManager;

  private alert: AlertManager;

  private promoCodes: PromoCodesManager;

  private inboundPromoCodes: InboundPromoCodesManager;

  private updateBilling: UpdateBillingManager;

  private paymentProcessingInfoManager: PaymentProcessingInfoManager;

  private payflowTopBarManager: PayflowTopBarManager;

  // init methods
  public paymentButtonInit: PaymentButtonManager['paymentButtonInit'];

  public pricingConfiguratorInit: PricingConfiguratorManager['pricingConfiguratorInit'];

  public prepaySelectInit: PrepaySelectManager['prepaySelectInit'];

  public contactsTierSelectInit: ContactsTierSelectManager['contactsTierSelectInit'];

  public currencyInit: CurrencyManager['currencyInit'];

  public nonProfitInit: NonProfitManager['nonProfitInit'];

  public billingInfoFormInit: BillingInfoFormManager['billingInfoFormInit'];

  public paymentMethodsInit: PaymentMethodsManager['paymentMethodsInit'];

  public thankYouInit: ThankYouManager['thankYouInit'];

  public emailPlansInit: EmailPlansManager['emailPlansInit'];

  public routerNonUiInit: RouterNonUiManager['routerNonUiInit'];

  public orderSummaryInit: OrderSummaryManager['orderSummaryInit'];

  public alertInit: AlertManager['alertInit'];

  public promoCodesInit: PromoCodesManager['promoCodesInit'];

  public inboundPromoCodesInit: InboundPromoCodesManager['inboundPromoCodesInit'];

  public paymentProcessingInfoInit: PaymentProcessingInfoManager['paymentProcessingInfoInit'];

  public payflowTopBarInit: PayflowTopBarManager['payflowTopBarInit'];

  private urlAdapter = new CentralizedUrlAdapter();

  constructor() {
    buyNowQueryParamsSessionStorage.restoreMissingParamsToUrl();
    buyNowQueryParamsSessionStorage.saveFromSearchParams(window.location.search);

    this.pricingConfigurator = new PricingConfiguratorManager();

    this.prepaySelect = new PrepaySelectManager(this.urlAdapter);
    this.contactsTierSelect = new ContactsTierSelectManager(this.urlAdapter);
    this.currency = new CurrencyManager(this.urlAdapter);
    this.nonProfit = new NonProfitManager(this.urlAdapter);

    this.orderSummary = new OrderSummaryManager();
    this.paymentButton = new PaymentButtonManager();

    this.billingInfoForm = new BillingInfoFormManager();
    this.paymentMethods = new PaymentMethodsManager(this.urlAdapter);

    this.thankYou = new ThankYouManager();
    this.emailPlans = new EmailPlansManager(this.urlAdapter);

    this.router = new RouterNonUiManager();
    this.alert = new AlertManager();

    this.promoCodes = new PromoCodesManager();
    this.inboundPromoCodes = new InboundPromoCodesManager(this.urlAdapter);
    this.updateBilling = new UpdateBillingManager();

    this.paymentProcessingInfoManager = new PaymentProcessingInfoManager();
    this.payflowTopBarManager = new PayflowTopBarManager(this.urlAdapter);

    this.paymentButtonInit = this.paymentButton.paymentButtonInit;
    this.pricingConfiguratorInit = this.pricingConfigurator.pricingConfiguratorInit;
    this.prepaySelectInit = this.prepaySelect.prepaySelectInit;
    this.contactsTierSelectInit = this.contactsTierSelect.contactsTierSelectInit;
    this.currencyInit = this.currency.currencyInit;
    this.nonProfitInit = this.nonProfit.nonProfitInit;
    this.billingInfoFormInit = this.billingInfoForm.billingInfoFormInit;
    this.paymentMethodsInit = this.paymentMethods.paymentMethodsInit;
    this.thankYouInit = this.thankYou.thankYouInit;
    this.emailPlansInit = this.emailPlans.emailPlansInit;
    this.routerNonUiInit = this.router.routerNonUiInit;
    this.orderSummaryInit = this.orderSummary.orderSummaryInit;
    this.alertInit = this.alert.alertInit;
    this.promoCodesInit = this.promoCodes.promoCodesInit;
    this.inboundPromoCodesInit = this.inboundPromoCodes.inboundPromoCodesInit;
    this.paymentProcessingInfoInit = this.paymentProcessingInfoManager.paymentProcessingInfoInit;
    this.payflowTopBarInit = this.payflowTopBarManager.payflowTopBarInit;

    this.redirectToPayflowIfConditionsNotMet(this.emailPlans, this.router);

    this.listeners = this.initializeControllers();
  }

  private initializeControllers(): Listeners {
    const controllers = [
      new PricingConfiguratorController(this.paymentButton),
      new PaymentButtonController(
        this.paymentMethods,
        this.billingInfoForm,
        this.router,
        this.paymentButton,
        this.nonProfit,
        this.currency,
        this.prepaySelect,
        this.contactsTierSelect,
        this.orderSummary,
        this.alert,
        this.emailPlans,
        this.paymentProcessingInfoManager
      ),
      new BillingInfoFormController(this.billingInfoForm, this.paymentMethods, this.orderSummary, this.paymentButton),
      new BillingFormSubmissionActionsController(this.updateBilling),
      new OrderSummaryController(this.paymentButton, this.router, undefined, undefined, this.prepaySelect),
      new PaymentMethodsController(this.paymentMethods, this.alert, undefined, this.orderSummary, this.paymentButton),
      new UpdateBillingController(this.paymentMethods, this.billingInfoForm, this.updateBilling, this.alert),
      new OrderSummarySetupController(
        this.orderSummary,
        this.billingInfoForm,
        this.nonProfit,
        this.currency,
        this.prepaySelect,
        this.contactsTierSelect,
        this.emailPlans,
        this.promoCodes
      ),
      new PayflowTrackActionsController(this.emailPlans, this.contactsTierSelect, this.nonProfit, this.prepaySelect),
      new PromoCodesController(this.orderSummary, this.promoCodes, this.inboundPromoCodes),
      new InboundPromoCodeApplicationController(this.orderSummary, this.promoCodes),
    ];

    initializerHelpers.initControllers(controllers);

    return initializerHelpers.createListeners(controllers);
  }

  public getFosTrackingIframeSrc = () => {
    const isGoogleSSO = urlParams.getGoogleSso() === 'true';

    if (isGoogleSSO) {
      const accountId = urlParams.getDistUiAccountId();

      return `https://www${envs.getEnv()}constantcontact.com/sso-signup/processing?socialProvider=google&account_id=${
        accountId ?? ''
      }`;
    }

    return `https://www${envs.getEnv()}constantcontact.com/post-signup/processing?formId=buy-form-em-pwd-v2-pricing`;
  };

  // ----------------------------
  // Private coordination methods
  // ----------------------------

  private async redirectToPayflowIfConditionsNotMet(
    emailPlansManager: EmailPlansManager,
    routerManager: RouterNonUiManager
  ) {
    const api = await emailPlansManager.getApiWhenReady();

    const { emailPlanType, emailPlanId } = this.urlAdapter.getEmailParams();
    const hasPlanInUrl = !!api.getSelectedPlanId() || !!emailPlanType || !!emailPlanId;
    const noPlanIdInUrl = !hasPlanInUrl;
    const notTrialer = !api.getIsTrialer();

    if (noPlanIdInUrl || notTrialer) {
      routerManager.getApiWhenReady().then((routerApi) => routerApi.goToPayflowEntryFromBuyNow({ replace: true }));
    }
  }
}
